import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { PillTone } from '../components/ui';
import { CommentsToggle } from './comments/CommentsToggle';
import { ModeSwitch } from './ModeSwitch';
import { StatusPill } from './StatusPill';
import { getInitials, type LockedModes, type ModeOption } from './modes';
import type { WorkspaceMode } from './urlState';

/**
 * Top_Bar: 56px, white, bottom border.
 * Logo symbol (home) · back link labelled with the Feature name · divider ·
 * item title · Mode_Switch · Status_Pill; Comments toggle with count and user
 * avatar on the right.
 *
 * Requirement: 3.2.
 */
export interface TopBarProps {
  /** Feature display name, for example "Due Diligence". Labels the back link. */
  featureName: string;
  /** Route of the Feature home. */
  backTo: string;
  /** Item title (job, loan or search name). */
  title: string;
  modes: readonly ModeOption[];
  mode: WorkspaceMode;
  locked?: LockedModes;
  onModeChange: (mode: WorkspaceMode) => void;
  statusLabel?: string;
  statusTone?: PillTone;
  /** Show the Comments toggle. Off until the comments API exists (feature flag). */
  commentsEnabled?: boolean;
  commentCount?: number;
  commentsOpen?: boolean;
  onToggleComments?: () => void;
  userName?: string;
}

export const TOP_BAR_HEIGHT_PX = 56;

export const TopBar: React.FC<TopBarProps> = ({
  featureName,
  backTo,
  title,
  modes,
  mode,
  locked,
  onModeChange,
  statusLabel,
  statusTone,
  commentsEnabled = false,
  commentCount = 0,
  commentsOpen = false,
  onToggleComments,
  userName,
}) => (
  <header className="flex h-14 shrink-0 items-center gap-4 border-b border-gray-200 bg-white px-4">
    <div className="flex min-w-0 items-center gap-3">
      <Link
        to="/"
        aria-label="Clairvoya home"
        title="Home"
        className="inline-flex shrink-0 items-center rounded-lg p-1 hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
      >
        <img src="/clairvoya_symbol_color.png" alt="" className="h-8 w-auto" />
      </Link>
      <Link
        to={backTo}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-bold text-[#5A21C9] hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
      >
        <ArrowLeft size={15} strokeWidth={2} aria-hidden="true" />
        <span>{featureName}</span>
      </Link>
      <span aria-hidden="true" className="h-6 w-px shrink-0 bg-gray-200" />
      <h1 className="truncate text-base font-bold text-gray-900" title={title}>
        {title}
      </h1>
    </div>
    {/* min-w-max keeps the Mode_Switch whole: a long title truncates instead. */}
    <div className="flex min-w-max shrink-0 items-center gap-3">
      <ModeSwitch modes={modes} active={mode} locked={locked} onChange={onModeChange} />
      {statusLabel ? <StatusPill label={statusLabel} tone={statusTone} /> : null}
    </div>

    <div className="ml-auto flex shrink-0 items-center gap-3">
      {commentsEnabled ? (
        <CommentsToggle count={commentCount} open={commentsOpen} onToggle={onToggleComments} />
      ) : null}
      <span
        role="img"
        aria-label={userName ? `Signed in as ${userName}` : 'Signed in user'}
        title={userName}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-[#834DFB] text-xs font-bold text-white"
      >
        {getInitials(userName)}
      </span>
    </div>
  </header>);

export default TopBar;
